import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { updateMetaTags, defaultMeta } from '../utils/metaTags';

const TermsOfService = () => {
  useEffect(() => {
    updateMetaTags({
      title: 'Terms of Service | UK Salary Toolkit',
      description: 'Terms of Service for UK Salary Toolkit. Read our terms and conditions for using our free UK tax calculator and salary tools.',
      canonical: `${defaultMeta.siteUrl}/terms-of-service`,
    });
  }, []);

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <motion.section 
        className="text-center space-y-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900">Terms of Service</h1>
        <p className="text-gray-600">Last updated: January 27, 2025</p>
      </motion.section>

      <motion.div 
        className="bg-white rounded-xl p-6 md:p-8 shadow-sm border space-y-6 text-gray-700"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h2>
          <p className="mb-4">
            By accessing and using UK Salary Toolkit ("the Website"), you accept and agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our website.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Use of Service</h2>
          <p className="mb-4">
            UK Salary Toolkit provides free tax calculation tools and information for educational and informational purposes only. You may use our calculators and tools for personal, non-commercial purposes.
          </p>
          <p className="mb-4">
            You agree not to:
          </p>
          <ul className="list-disc list-inside space-y-1 mb-4 ml-4">
            <li>Use the website for any illegal or unauthorized purpose</li>
            <li>Attempt to gain unauthorized access to any part of the website</li>
            <li>Interfere with or disrupt the website or servers</li>
            <li>Reproduce, duplicate, or copy content without permission</li>
            <li>Use automated systems to access the website excessively</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Not Financial or Tax Advice</h2>
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
            <p className="font-semibold text-yellow-900 mb-2">Important Disclaimer:</p>
            <p className="text-yellow-800">
              The information and calculations provided by UK Salary Toolkit are for informational and educational purposes only. They do not constitute financial, tax, or legal advice.
            </p>
          </div>
          <p className="mb-4">
            Our calculators provide estimates based on standard UK tax rates for the 2025/26 tax year. Actual tax deductions may vary based on:
          </p>
          <ul className="list-disc list-inside space-y-1 mb-4 ml-4">
            <li>Individual circumstances and tax codes</li>
            <li>Benefits in kind and other taxable benefits</li>
            <li>Pension contributions and salary sacrifice arrangements</li>
            <li>Marriage allowance and other tax reliefs</li>
            <li>Changes in tax legislation</li>
          </ul>
          <p>
            Always consult with a qualified tax advisor, accountant, or HMRC for accurate tax calculations and advice specific to your situation.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Accuracy of Information</h2>
          <p className="mb-4">
            We strive to provide accurate and up-to-date information based on official HMRC rates for the 2025/26 tax year. However, we make no warranties or representations regarding the accuracy, completeness, or reliability of any information on this website.
          </p>
          <p>
            Tax rates and thresholds may change, and we cannot guarantee that all information is current at all times. You should verify important information with official sources such as <a href="https://www.gov.uk" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">GOV.UK</a> or HMRC.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Limitation of Liability</h2>
          <p className="mb-4">
            To the fullest extent permitted by law, UK Salary Toolkit, its owners, operators, and affiliates shall not be liable for any direct, indirect, incidental, special, consequential, or punitive damages arising from:
          </p>
          <ul className="list-disc list-inside space-y-1 mb-4 ml-4">
            <li>Your use or inability to use the website</li>
            <li>Any errors or omissions in the content</li>
            <li>Any decisions made based on information from this website</li>
            <li>Loss of data or profits</li>
          </ul>
          <p>
            Our total liability shall not exceed the amount you paid to use this service (which is £0, as the service is free).
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Intellectual Property</h2>
          <p className="mb-4">
            All content on UK Salary Toolkit, including text, graphics, logos, and software, is the property of UK Salary Toolkit or its content suppliers and is protected by UK and international copyright laws.
          </p>
          <p>
            You may not reproduce, distribute, modify, or create derivative works from any content without express written permission.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Third-Party Links</h2>
          <p>
            Our website may contain links to third-party websites (e.g., GOV.UK, HMRC). We are not responsible for the content, privacy policies, or practices of these external sites. Your use of third-party websites is at your own risk.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Modifications to Service</h2>
          <p>
            We reserve the right to modify, suspend, or discontinue any part of the website at any time without notice. We shall not be liable to you or any third party for any modification, suspension, or discontinuance.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Indemnification</h2>
          <p>
            You agree to indemnify and hold harmless UK Salary Toolkit, its owners, operators, and affiliates from any claims, damages, losses, liabilities, and expenses (including legal fees) arising from your use of the website or violation of these terms.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Governing Law</h2>
          <p>
            These Terms of Service shall be governed by and construed in accordance with the laws of England and Wales. Any disputes arising from these terms shall be subject to the exclusive jurisdiction of the courts of England and Wales.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Changes to Terms</h2>
          <p>
            We reserve the right to modify these Terms of Service at any time. Changes will be effective immediately upon posting on this page. Your continued use of the website after changes are posted constitutes acceptance of the modified terms.
          </p>
        </section>

      </motion.div>
    </div>
  );
};

export default TermsOfService;

